import { useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { describeGift, GEM_DUST_ID } from "components/admin/GiftEditor";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth, useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";
import {
  AchievementDialog,
  EditItemDialog,
  EditPetDialog,
  RemoveItemDialog,
  TeleportDialog,
} from "layouts/players/AssetDialogs";
import {
  BanDialog,
  BuffItemDialog,
  CurrencyDialog,
  DeletePlayerDialog,
  ItemDialog,
  MessageDialog,
  MuteDialog,
  PasswordDialog,
  PetDialog,
} from "layouts/players/PlayerActions";
import { PetCoinDialog, PetCoinTab } from "layouts/players/PetCoin";
import PlayerVipTab from "layouts/players/Vip";

/*
 * Bố cục trang (người dùng chính: CSKH / vận hành, không rành kỹ thuật):
 *   Đầu trang: tên + trạng thái → đang ở đâu → 5 ô tiền → nút thao tác chia 3 nhóm
 *              (Tặng & cộng / Liên lạc / Xử lý vi phạm), nút bị mờ có dòng giải thích ngay dưới.
 *   Tab (ghi lên URL ?tab=): Tổng quan · Hành trang · Pet · Danh hiệu · Sổ Pet Coin · VIP · Hòm thư · Đánh boss · Lịch sử
 *   Tổng quan: [Nhân vật] [Điểm] [Tài khoản + Buff] / [Đăng nhập gần đây]
 *              / "Nâng cao" gập sẵn: đặt mật khẩu, quà gộp server, quyền admin, xoá.
 *   Mã số (user id, id nhân vật, id món đồ) chỉ hiện chữ nhỏ hoặc khi rê chuột, để báo dev.
 */

function InfoList({ rows }) {
  return (
    <MDBox>
      {rows
        .filter((row) => row)
        .map(([label, value]) => (
          <MDBox key={label} display="flex" py={0.75} justifyContent="space-between" gap={2}>
            <MDTypography variant="button" fontWeight="bold" color="text">
              {label}
            </MDTypography>
            <MDTypography
              variant="button"
              color="text"
              textAlign="right"
              sx={{ wordBreak: "break-word" }}
            >
              {value ?? "-"}
            </MDTypography>
          </MDBox>
        ))}
    </MDBox>
  );
}

InfoList.propTypes = {
  // Dòng `false` (điều kiện không thoả) bị bỏ qua.
  rows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.array, PropTypes.bool])).isRequired,
};

const LOGIN_PREVIEW = 5;

function StatTile({ label, value }) {
  return (
    <MDBox
      px={1.5}
      py={1}
      sx={{ border: 1, borderColor: "divider", borderRadius: 2, height: "100%" }}
    >
      <MDTypography variant="caption" color="text" display="block">
        {label}
      </MDTypography>
      <MDTypography variant="h6">{value}</MDTypography>
    </MDBox>
  );
}

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

/** Một nhóm nút thao tác có nhãn bên trái, dòng giải thích (vì sao nút bị mờ) bên dưới. */
function ActionGroup({ label, notes, children }) {
  const visible = notes.filter(Boolean);
  return (
    <MDBox py={1}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={2}>
          <MDTypography variant="button" fontWeight="bold" color="text">
            {label}
          </MDTypography>
        </Grid>
        <Grid item xs={12} md={10}>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {children}
          </MDBox>
          {visible.map((note) => (
            <MDTypography key={note} variant="caption" color="text" display="block" mt={0.5}>
              <Icon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }}>
                info
              </Icon>
              {note}
            </MDTypography>
          ))}
        </Grid>
      </Grid>
    </MDBox>
  );
}

ActionGroup.defaultProps = { notes: [] };
ActionGroup.propTypes = {
  label: PropTypes.string.isRequired,
  notes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool])),
  children: PropTypes.node.isRequired,
};

function ActionButton({ icon, label, help, color, variant, disabled, onClick, to }) {
  return (
    <Tooltip title={help}>
      <span>
        <MDButton
          size="small"
          variant={variant}
          color={color}
          disabled={disabled}
          onClick={onClick}
          {...(to ? { component: Link, to } : {})}
        >
          <Icon>{icon}</Icon>&nbsp;{label}
        </MDButton>
      </span>
    </Tooltip>
  );
}

ActionButton.defaultProps = {
  variant: "gradient",
  disabled: false,
  onClick: undefined,
  to: undefined,
};
ActionButton.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string,
};

/** Một dòng trong khu "Nâng cao": tên việc + giải thích + nút. */
function AdvancedRow({ title, description, children }) {
  return (
    <MDBox py={1.5}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md>
          <MDTypography variant="button" fontWeight="bold" display="block">
            {title}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {description}
          </MDTypography>
        </Grid>
        <Grid item xs={12} md="auto">
          {children}
        </Grid>
      </Grid>
    </MDBox>
  );
}

AdvancedRow.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

const statsText = (s) => `${s.atk} / ${s.def} / ${s.hp} / ${s.mp}`;

const gemStatsText = (stats) => {
  if (!stats?.length) return "";
  if (stats.every((s) => s.percent === stats[0].percent)) {
    return `+${stats[0].percent}% ${stats.map((s) => s.name).join("/")}`;
  }
  return stats.map((s) => `+${s.percent}% ${s.name}`).join(", ");
};

const renderGem = (i) => {
  if (i.gemStats) {
    return `${i.gemElement} · ${gemStatsText(i.gemStats)}`;
  }
  if (!i.gems?.length) return "-";
  return (
    <MDBox display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
      {i.gems.map((gem) => (
        <Tooltip
          key={gem.slot}
          title={[
            `Lỗ ${gem.slot}`,
            `Hệ ${gem.element}`,
            gemStatsText(gem.stats),
            !gem.canTrade && "Khoá giao dịch",
            `mã mẫu ${gem.templateId}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        >
          <Chip
            size="small"
            color="info"
            label={`${gem.name}${gem.lvl > 0 ? ` +${gem.lvl}` : ""}`}
          />
        </Tooltip>
      ))}
    </MDBox>
  );
};

const itemColumns = (petNames) => [
  {
    key: "name",
    label: "Vật phẩm",
    render: (i) => (
      <Tooltip
        title={[
          i.sources?.length ? `Nguồn: ${i.sources.join(", ")}` : null,
          `mã món ${i.itemId} · mã mẫu ${i.templateId}`,
        ]
          .filter(Boolean)
          .join(" · ")}
      >
        <span>
          {i.name}
          {i.lvl > 0 ? ` +${i.lvl}` : ""}
        </span>
      </Tooltip>
    ),
  },
  { key: "count", label: "Số lượng", align: "right", render: (i) => formatNumber(i.count) },
  {
    key: "stats",
    label: "Công / Thủ / HP / MP",
    render: (i) => {
      if (i.stackable || i.gemStats) return "-";
      const base = statsText(i);
      if (!i.final || statsText(i.final) === base) return base;
      return (
        <Tooltip title={`Gốc: ${base} · Đã tính cường hoá, ngọc khảm, dung hợp`}>
          <span>{statsText(i.final)}</span>
        </Tooltip>
      );
    },
  },
  { key: "gem", label: "Ngọc khảm", wrap: true, render: renderGem },
  {
    key: "numFusion",
    label: "Dung hợp",
    align: "right",
    render: (i) => (i.stackable ? "-" : i.numFusion),
  },
  {
    key: "canTrade",
    label: "Giao dịch",
    render: (i) =>
      i.canTrade ? (
        <Chip size="small" color="success" label="Được" />
      ) : (
        <Chip size="small" label="Khoá" />
      ),
  },
  {
    key: "expire",
    label: "Hết hạn",
    render: (i) => (i.expire > 0 ? formatDate(i.expire) : "Vĩnh viễn"),
  },
  {
    key: "petEquipId",
    label: "Pet đang đeo",
    render: (i) => (i.petEquipId > 0 ? petNames[i.petEquipId] || "Pet khác" : "-"),
  },
];

function OnlineEditNote() {
  return (
    <MDBox px={2} pt={2}>
      <Alert severity="info">
        Người chơi đang trong game nên ở đây chỉ thu hồi được. Sửa vật phẩm, sửa pet, cấp danh hiệu
        và buff trang bị chỉ làm được khi người chơi đã thoát game.
      </Alert>
    </MDBox>
  );
}

function InventoryTab({ player, onEdit, onRemove }) {
  const [selected, setSelected] = useState(0);
  const inventories = player.inventories || [];
  const current = inventories[selected];
  const petNames = Object.fromEntries((player.pets || []).map((p) => [p.petId, p.name]));
  const inlaid = inventories.reduce(
    (sum, inv) => sum + inv.items.reduce((n, item) => n + (item.gems?.length || 0), 0),
    0
  );
  const gemDust = inventories.reduce(
    (sum, inv) =>
      sum +
      inv.items
        .filter((item) => item.templateId === GEM_DUST_ID)
        .reduce((n, item) => n + (item.count || 0), 0),
    0
  );
  return (
    <>
      <MDBox px={2} pb={1} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        {inventories.map((inv, index) => (
          <Chip
            key={inv.type}
            label={`${inv.name} (${inv.items.length})`}
            color={index === selected ? "info" : "default"}
            onClick={() => setSelected(index)}
          />
        ))}
        {inlaid > 0 && (
          <Chip variant="outlined" color="info" label={`Ngọc đang khảm trên đồ: ${inlaid}`} />
        )}
        {gemDust > 0 && (
          <Chip variant="outlined" color="info" label={`Bụi ngọc: ${formatNumber(gemDust)}`} />
        )}
        {player.skin && <Chip variant="outlined" label={`Đang mặc: ${player.skin.name}`} />}
        {player.wing && <Chip variant="outlined" label={`Cánh: ${player.wing.name}`} />}
      </MDBox>
      <AdminTable
        rows={current?.items || []}
        rowKey="itemId"
        columns={[
          ...itemColumns(petNames),
          (onEdit || onRemove) && {
            key: "actions",
            label: "",
            align: "right",
            render: (i) => (
              <MDBox display="flex" justifyContent="flex-end">
                {onEdit && (
                  <Tooltip title="Sửa: khoá / mở giao dịch, đặt sao dung hợp">
                    <IconButton
                      size="small"
                      color="warning"
                      aria-label="Sửa"
                      onClick={() => onEdit(current.type, i)}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  </Tooltip>
                )}
                {onRemove && (
                  <Tooltip title="Thu hồi (xoá món này khỏi túi)">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Thu hồi"
                      onClick={() => onRemove(current.type, i)}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </Tooltip>
                )}
              </MDBox>
            ),
          },
        ].filter(Boolean)}
        emptyText="Túi này trống"
      />
    </>
  );
}

InventoryTab.defaultProps = { onEdit: null, onRemove: null };
InventoryTab.propTypes = {
  player: PropTypes.shape({
    inventories: PropTypes.arrayOf(PropTypes.object),
    pets: PropTypes.arrayOf(PropTypes.object),
    skin: PropTypes.object,
    wing: PropTypes.object,
  }).isRequired,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
};

function MailboxTab({ userId }) {
  const { data, loading, error } = useFetch(
    () => api.get(`/api/admin/players/${userId}/mailbox`, { limit: 100 }),
    [userId]
  );
  return (
    <>
      {error && <Alert severity="error">{error}</Alert>}
      <AdminTable
        loading={loading}
        rows={data || []}
        rowKey={(m) => `${m.isGlobal ? "g" : "p"}${m.id}`}
        emptyText="Hòm thư trống"
        columns={[
          {
            key: "title",
            label: "Tiêu đề",
            wrap: true,
            render: (m) => (
              <Tooltip title={`${m.content || ""} · mã thư ${m.isGlobal ? "G" : ""}${m.id}`}>
                <span>{m.title}</span>
              </Tooltip>
            ),
          },
          { key: "sender", label: "Người gửi" },
          {
            key: "gift",
            label: "Quà",
            wrap: true,
            render: (m) =>
              m.hasGift
                ? [
                    m.gift ? describeGift(m.gift) : null,
                    ...(m.items || []),
                    ...(m.pets || []).map((p) => `Pet ${p}`),
                  ]
                    .filter(Boolean)
                    .join(", ")
                : "-",
          },
          {
            key: "state",
            label: "Trạng thái",
            render: (m) => (
              <MDBox display="flex" gap={0.5} flexWrap="wrap">
                {m.isGlobal && <Chip size="small" variant="outlined" label="Thư toàn server" />}
                {m.isDeleted && <Chip size="small" label="Đã xoá" />}
                {m.isExpired && <Chip size="small" color="error" label="Hết hạn" />}
                {m.hasGift && (
                  <Chip
                    size="small"
                    color={m.isClaimed ? "success" : "warning"}
                    label={m.isClaimed ? "Đã nhận quà" : "Chưa nhận quà"}
                  />
                )}
                {!m.hasGift && <Chip size="small" label={m.isRead ? "Đã đọc" : "Chưa đọc"} />}
              </MDBox>
            ),
          },
          { key: "createTime", label: "Gửi lúc", render: (m) => formatDate(m.createTime) },
          {
            key: "expireTime",
            label: "Hết hạn",
            render: (m) => (m.expireTime ? formatDate(m.expireTime) : "Không"),
          },
        ]}
      />
    </>
  );
}

MailboxTab.propTypes = { userId: PropTypes.number.isRequired };

const BOSS_RESULTS = { KILL: "Boss bị hạ", TIMEOUT: "Hết giờ", SHUTDOWN: "Máy chủ tắt" };

function BossTab({ userId }) {
  const { data, loading, error } = useFetch(
    () => api.get(`/api/admin/players/${userId}/boss-log`, { limit: 100 }),
    [userId]
  );
  return (
    <>
      {error && <Alert severity="error">{error}</Alert>}
      <AdminTable
        loading={loading}
        rows={data || []}
        emptyText="Chưa đánh boss nào"
        columns={[
          { key: "createTime", label: "Thời gian", render: (b) => formatDate(b.createTime) },
          { key: "bossName", label: "Boss" },
          { key: "map", label: "Nơi đánh", render: (b) => `${b.mapName} khu ${b.zoneId}` },
          { key: "result", label: "Kết quả", render: (b) => BOSS_RESULTS[b.result] || b.result },
          {
            key: "damage",
            label: "Sát thương",
            align: "right",
            render: (b) => formatNumber(b.damage),
          },
          { key: "ticket", label: "Vé đã dùng", align: "right" },
          {
            key: "rank",
            label: "Hạng",
            align: "right",
            render: (b) => (b.rank > 0 ? b.rank : "-"),
          },
        ]}
      />
    </>
  );
}

BossTab.propTypes = { userId: PropTypes.number.isRequired };

function HistoryTab({ userId }) {
  const [page, setPage] = useState(1);
  const { data, loading, error } = useFetch(
    () => api.get("/api/admin/history", { userId, page, size: 50 }),
    [userId, page]
  );
  return (
    <>
      {error && <Alert severity="error">{error}</Alert>}
      <AdminTable
        loading={loading}
        rows={data?.items || []}
        rowKey="id"
        page={page}
        size={50}
        hasMore={data?.hasMore}
        onPageChange={setPage}
        emptyText="Chưa có lịch sử"
        columns={[
          { key: "time", label: "Thời gian", width: 170, render: (r) => formatDate(r.time) },
          { key: "log", label: "Nội dung", wrap: true },
        ]}
      />
    </>
  );
}

HistoryTab.propTypes = { userId: PropTypes.number.isRequired };

function PageShell({ children }) {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {children}
    </DashboardLayout>
  );
}

PageShell.propTypes = { children: PropTypes.node.isRequired };

function PlayerDetail() {
  const userId = Number(useParams().userId);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState(null);
  const [assetDialog, setAssetDialog] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showAllLogins, setShowAllLogins] = useState(false);
  const isOwner = !!useAuth().admin?.isOwner;
  const canModerate = useCan(PERMISSIONS.PLAYER_MODERATE);
  const canGive = useCan(PERMISSIONS.PLAYER_GIVE);
  const canPetCoin = useCan(PERMISSIONS.PETCOIN_GIVE);
  const canVip = useCan(PERMISSIONS.VIP_VIEW);
  const [petCoinKey, setPetCoinKey] = useState(0);
  const canAccount = useCan(PERMISSIONS.PLAYER_ACCOUNT);
  const canUnmute = useCan(PERMISSIONS.CHAT_EDIT) || canModerate;
  const canMail = useCan(PERMISSIONS.MAIL_SEND);
  const canMarket = useCan(PERMISSIONS.MARKET_VIEW);
  const canHistory = useCan(PERMISSIONS.HISTORY_VIEW);
  const { data, loading, error, reload } = useFetch(
    () => api.get(`/api/admin/players/${userId}`),
    [userId]
  );

  if (loading && !data) {
    return (
      <PageShell>
        <MDBox display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </MDBox>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <MDBox py={3}>
          <Alert severity="error">
            {error
              ? `Không mở được người chơi này: ${error}`
              : "Không tìm thấy người chơi này. Có thể tài khoản đã bị xoá."}
          </Alert>
          <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
            <MDButton variant="gradient" color="info" onClick={() => reload(true)}>
              <Icon>refresh</Icon>&nbsp;Thử lại
            </MDButton>
            <MDButton variant="outlined" color="info" component={Link} to="/players">
              <Icon>arrow_back</Icon>&nbsp;Về danh sách người chơi
            </MDButton>
          </MDBox>
        </MDBox>
      </PageShell>
    );
  }

  const { account, player, online, location, buff, logins = [], chatMute } = data;
  const closeDialog = () => setDialog(null);
  const closeAssetDialog = () => setAssetDialog(null);
  const refresh = () => reload(true);
  const displayName = player?.name || account.username;

  // Tab ghi lên URL để F5 hoặc gửi link vẫn mở đúng tab.
  const tabs = [
    { value: "overview", label: "Tổng quan" },
    player && { value: "inventory", label: "Hành trang" },
    player && { value: "pets", label: `Pet (${player.pets?.length || 0})` },
    player && {
      value: "achievements",
      label: `Danh hiệu (${player.achievementList?.length || 0})`,
    },
    { value: "petcoin", label: "Sổ Pet Coin" },
    canVip && { value: "vip", label: "VIP" },
    player && { value: "mailbox", label: "Hòm thư" },
    { value: "boss", label: "Đánh boss" },
    canHistory && { value: "history", label: "Lịch sử" },
  ].filter(Boolean);
  const requestedTab = params.get("tab");
  const tab = tabs.some((t) => t.value === requestedTab) ? requestedTab : "overview";
  const setTab = (value) => {
    const next = new URLSearchParams(params);
    if (value === "overview") next.delete("tab");
    else next.set("tab", value);
    setParams(next, { replace: true });
  };

  const simpleAction = async (path, title, message, color = "error", body = undefined) => {
    if (!(await confirm({ title, message, color, confirmText: title }))) return;
    const { ok } = await run(
      () => api.post(path, body),
      (res) => (typeof res === "string" ? res : "Đã xong")
    );
    if (ok) refresh();
  };

  const toggleBuff = (kind, enable) =>
    run(
      () => api.post(`/api/admin/players/${userId}/buff-flag`, { kind, enable }),
      enable ? "Đã bật buff" : "Đã tắt buff"
    ).then(refresh);

  const removeAchievement = (a) =>
    simpleAction(
      `/api/admin/players/${userId}/achievements/${a.id}/remove`,
      "Thu hồi danh hiệu",
      `Thu hồi danh hiệu "${a.name}" của ${displayName}? Người chơi sẽ mất danh hiệu này ngay.`
    );

  const setAdminFlag = (isAdmin) =>
    simpleAction(
      `/api/admin/players/${userId}/admin-flag`,
      isAdmin ? "Cấp quyền admin" : "Gỡ quyền admin",
      isAdmin
        ? `Cho ${displayName} quyền admin TOÀN PHẦN: làm GM trong game và làm chủ web admin (mọi chức năng, kể cả phân quyền).\n\nChỉ cấp cho người thật sự tin tưởng. Chắc chắn?`
        : `Gỡ quyền admin của ${displayName}? Họ sẽ thành người chơi thường và mất quyền vào web admin bằng nhân vật này.`,
      isAdmin ? "error" : "warning",
      { isAdmin }
    );

  const noPlayerNote = !player && "Tài khoản này chưa tạo nhân vật nên chưa tặng được đồ.";
  const currentAchievement = player?.achievementList?.find((a) => a.current);

  const giveButtons = [
    canGive && (
      <ActionButton
        key="currency"
        icon="paid"
        label="Cộng / trừ tiền"
        help="Cộng hoặc trừ vàng, ngọc, lúa, năng lượng"
        color="success"
        disabled={busy || !player}
        onClick={() => setDialog("currency")}
      />
    ),
    canPetCoin && (
      <ActionButton
        key="petcoin"
        icon="toll"
        label="Cộng / trừ Pet Coin"
        help="Pet Coin là tiền nạp của tài khoản; phải ghi lý do"
        color="warning"
        disabled={busy}
        onClick={() => setDialog("petcoin")}
      />
    ),
    canGive && (
      <ActionButton
        key="item"
        icon="inventory"
        label="Tặng vật phẩm"
        help="Gửi vật phẩm vào hòm thư, người chơi tự bấm nhận"
        color="info"
        disabled={busy || !player}
        onClick={() => setDialog("item")}
      />
    ),
    canGive && (
      <ActionButton
        key="pet"
        icon="pets"
        label="Tặng pet"
        help="Gửi pet mới vào hòm thư, người chơi tự bấm nhận"
        color="info"
        disabled={busy || !player}
        onClick={() => setDialog("pet")}
      />
    ),
    canMail && (
      <ActionButton
        key="mail"
        icon="mail"
        label="Gửi thư"
        help="Mở trang Thư & quà, người nhận điền sẵn là người chơi này"
        color="info"
        variant="outlined"
        disabled={!player}
        to={`/mail?userId=${userId}`}
      />
    ),
    canGive && (
      <ActionButton
        key="buffItem"
        icon="auto_fix_high"
        label="Buff trang bị"
        help="Tạo sẵn trang bị đã cường hoá / tiến hoá / dung hợp, cất thẳng vào túi"
        color="warning"
        variant="outlined"
        disabled={busy || !player || online}
        onClick={() => setDialog("buffItem")}
      />
    ),
  ].filter(Boolean);

  const contactButtons = canModerate
    ? [
        <ActionButton
          key="message"
          icon="chat"
          label="Nhắn tin"
          help="Hiện một thông báo lên màn hình người chơi"
          color="dark"
          disabled={busy || !online}
          onClick={() => setDialog("message")}
        />,
        <ActionButton
          key="teleport"
          icon="near_me"
          label="Dịch chuyển"
          help="Đưa người chơi tới map / khu khác (ví dụ khi bị kẹt)"
          color="dark"
          disabled={busy || !online}
          onClick={() => setDialog("teleport")}
        />,
      ]
    : [];

  const moderateButtons = [
    canMarket && player && (
      <ActionButton
        key="market"
        icon="storefront"
        label="Đồ treo ki ốt"
        help="Xem người chơi này đang treo bán gì ở chợ, gỡ món có vấn đề"
        color="dark"
        variant="outlined"
        to={`/market?seller=${userId}&sellerName=${encodeURIComponent(player.name || "")}`}
      />
    ),
    online && canModerate && (
      <ActionButton
        key="kick"
        icon="logout"
        label="Đá khỏi game"
        help="Ngắt kết nối ngay, người chơi vẫn đăng nhập lại được"
        color="error"
        variant="outlined"
        disabled={busy}
        onClick={() =>
          simpleAction(
            `/api/admin/players/${userId}/kick`,
            "Đá khỏi game",
            `Ngắt kết nối ${displayName} ngay bây giờ? Họ vẫn đăng nhập lại được bình thường.`
          )
        }
      />
    ),
    chatMute && canUnmute && (
      <ActionButton
        key="unmute"
        icon="chat"
        label="Gỡ khoá chat"
        help="Cho người chơi chat lại ngay, không chờ hết hạn"
        color="success"
        variant="outlined"
        disabled={busy}
        onClick={() =>
          simpleAction(
            `/api/admin/players/${userId}/unmute`,
            "Gỡ khoá chat",
            `Cho ${displayName} chat lại ngay?`,
            "success"
          )
        }
      />
    ),
    !chatMute && canModerate && (
      <ActionButton
        key="mute"
        icon="speaker_notes_off"
        label="Khoá chat"
        help="Cấm chat có thời hạn, vẫn chơi bình thường"
        color="warning"
        variant="outlined"
        disabled={busy || !player}
        onClick={() => setDialog("mute")}
      />
    ),
    canModerate && account.banned && (
      <ActionButton
        key="unban"
        icon="lock_open"
        label="Mở khoá tài khoản"
        help="Cho tài khoản đăng nhập lại"
        color="success"
        disabled={busy}
        onClick={() =>
          simpleAction(
            `/api/admin/players/${userId}/unban`,
            "Mở khoá tài khoản",
            `Cho tài khoản ${account.username} đăng nhập lại ngay?`,
            "success"
          )
        }
      />
    ),
    canModerate && !account.banned && (
      <ActionButton
        key="ban"
        icon="block"
        label="Khoá tài khoản"
        help="Chặn đăng nhập, có thời hạn hoặc vĩnh viễn"
        color="error"
        disabled={busy}
        onClick={() => setDialog("ban")}
      />
    ),
  ].filter(Boolean);

  const deleteBlockedReason =
    (online && "Người chơi đang trong game — chờ họ thoát rồi mới xoá được.") ||
    (player?.isAdmin && "Không xoá được nhân vật admin. Gỡ quyền admin trước.") ||
    null;

  const advancedRows = [
    canAccount && (
      <AdvancedRow
        key="password"
        title="Đặt mật khẩu mới"
        description="Dùng khi khách quên mật khẩu và đã xác minh đúng là chủ tài khoản."
      >
        <MDButton
          size="small"
          variant="outlined"
          color="warning"
          onClick={() => setDialog("password")}
        >
          <Icon>key</Icon>&nbsp;Đặt mật khẩu mới
        </MDButton>
      </AdvancedRow>
    ),
    canAccount && player?.isMergeServer && (
      <AdvancedRow
        key="merge"
        title="Cho nhận lại quà gộp server"
        description="Người chơi đã nhận quà gộp server. Bấm để họ nhận lại ở lần vào game tới (dùng khi nhận lỗi)."
      >
        <MDButton
          size="small"
          variant="outlined"
          color="warning"
          disabled={busy}
          onClick={() =>
            simpleAction(
              `/api/admin/players/${userId}/merge-reset`,
              "Cho nhận lại quà gộp server",
              `Cho ${displayName} nhận lại quà gộp server ở lần vào game tới?`,
              "warning"
            )
          }
        >
          <Icon>restart_alt</Icon>&nbsp;Cho nhận lại
        </MDButton>
      </AdvancedRow>
    ),
    isOwner && player && (
      <AdvancedRow
        key="admin"
        title={`Quyền admin game: ${player.isAdmin ? "đang có" : "không có"}`}
        description="Admin là GM trong game và là chủ web admin (có mọi quyền). Chỉ chủ web thấy mục này."
      >
        <MDButton
          size="small"
          variant="outlined"
          color={player.isAdmin ? "warning" : "error"}
          disabled={busy}
          onClick={() => setAdminFlag(!player.isAdmin)}
        >
          <Icon>{player.isAdmin ? "remove_moderator" : "admin_panel_settings"}</Icon>
          &nbsp;{player.isAdmin ? "Gỡ quyền admin" : "Cấp quyền admin"}
        </MDButton>
      </AdvancedRow>
    ),
    canAccount && (
      <AdvancedRow
        key="delete"
        title="Xoá nhân vật hoặc cả tài khoản"
        description={
          deleteBlockedReason ||
          "Xoá hẳn khỏi game (có sao lưu ra file trước khi xoá). Phải gõ lại tên để xác nhận."
        }
      >
        <MDButton
          size="small"
          variant="gradient"
          color="error"
          disabled={busy || !!deleteBlockedReason}
          onClick={() => setDialog("delete")}
        >
          <Icon>delete_forever</Icon>&nbsp;Xoá…
        </MDButton>
      </AdvancedRow>
    ),
  ].filter(Boolean);

  return (
    <DashboardLayout>
      <DashboardNavbar title={displayName} />
      <MDBox py={3}>
        <PageGuide page="players-detail" />

        {/* ---- Đầu trang: ai, đang ở đâu, có bao nhiêu tiền, làm được gì ---- */}
        <Card sx={{ p: 2, mb: 3 }}>
          <MDBox mb={1}>
            <MDButton
              variant="text"
              color="info"
              size="small"
              component={Link}
              to="/players"
              sx={{ px: 0 }}
            >
              <Icon>arrow_back</Icon>&nbsp;Danh sách người chơi
            </MDButton>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDTypography variant="h4">
              {player?.name || `${account.username} (chưa tạo nhân vật)`}
            </MDTypography>
            {online ? (
              <Chip size="small" color="success" label="Đang trong game" />
            ) : (
              <Chip size="small" label="Đã thoát game" />
            )}
            {player?.isAdmin && <Chip size="small" color="warning" label="Admin" />}
            {account.banned && (
              <Tooltip
                title={account.banReason ? `Lý do: ${account.banReason}` : "Không ghi lý do"}
              >
                <Chip
                  size="small"
                  color="error"
                  label={
                    account.banType === 2
                      ? "Tài khoản bị khoá vĩnh viễn"
                      : `Tài khoản bị khoá tới ${formatDate(account.banUntil)}`
                  }
                />
              </Tooltip>
            )}
            {chatMute && (
              <Tooltip title={chatMute.reason ? `Lý do: ${chatMute.reason}` : "Không ghi lý do"}>
                <Chip
                  size="small"
                  color="warning"
                  label={`Bị khoá chat tới ${formatDate(chatMute.until)}`}
                />
              </Tooltip>
            )}
          </MDBox>
          <MDTypography variant="button" color="text" display="block" mt={0.5}>
            {online && location
              ? `Đang ở ${location.mapName} khu ${location.zoneId}`
              : `Vào game lần cuối: ${player ? formatDate(player.lastOnline) : "chưa từng"}`}
            {` · Tài khoản ${account.username}`}
          </MDTypography>
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
            Mã người chơi {account.userId}
            {player && ` · mã nhân vật ${player.playerId}`} (dùng khi báo lỗi cho kỹ thuật)
          </MDTypography>

          <Grid container spacing={1} mt={1}>
            {player && (
              <>
                <Grid item xs={6} sm={4} md>
                  <StatTile label="Vàng" value={formatNumber(player.gold)} />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile label="Ngọc" value={formatNumber(player.coin)} />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile label="Lúa" value={formatNumber(player.lua)} />
                </Grid>
                <Grid item xs={6} sm={4} md>
                  <StatTile label="Năng lượng" value={formatNumber(player.star)} />
                </Grid>
              </>
            )}
            <Grid item xs={6} sm={4} md>
              <StatTile label="Pet Coin (tiền nạp)" value={formatNumber(account.vnd)} />
            </Grid>
          </Grid>

          {(giveButtons.length > 0 || contactButtons.length > 0 || moderateButtons.length > 0) && (
            <Divider sx={{ my: 2 }} />
          )}
          {giveButtons.length > 0 && (
            <ActionGroup
              label="Tặng & cộng"
              notes={[
                noPlayerNote,
                player &&
                  online &&
                  canGive &&
                  "Buff trang bị chỉ làm được khi người chơi đã thoát game.",
              ]}
            >
              {giveButtons}
            </ActionGroup>
          )}
          {contactButtons.length > 0 && (
            <ActionGroup
              label="Liên lạc"
              notes={[
                !online &&
                  "Người chơi đang không trong game — nhắn tin và dịch chuyển chỉ dùng được khi họ đang chơi.",
              ]}
            >
              {contactButtons}
            </ActionGroup>
          )}
          {moderateButtons.length > 0 && (
            <ActionGroup label="Xử lý vi phạm">{moderateButtons}</ActionGroup>
          )}

          <MDBox mt={1}>
            <Tabs
              value={tab}
              onChange={(e, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((t) => (
                <Tab
                  key={t.value}
                  value={t.value}
                  label={t.label}
                  // Theme cho tab co giãn lấp đầy hàng; màn hẹp thì giữ cỡ chữ để hàng tab cuộn ngang được.
                  sx={{ flex: { xs: "0 0 auto", lg: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" }}
                />
              ))}
            </Tabs>
          </MDBox>
        </Card>

        {tab === "overview" && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <PageCard title="Nhân vật" color="info">
                  {player ? (
                    <InfoList
                      rows={[
                        ["Giới tính", player.gender === 1 ? "Nữ" : "Nam"],
                        [
                          "Bang hội",
                          player.clanId > 0 ? (
                            <Tooltip key="clan" title={`mã bang ${player.clanId}`}>
                              <Link to={`/clans?clan=${player.clanId}`}>
                                {player.clanName || `Bang mã ${player.clanId}`}
                              </Link>
                            </Tooltip>
                          ) : (
                            "Chưa vào bang"
                          ),
                        ],
                        [
                          "Danh hiệu đang đeo",
                          currentAchievement ? currentAchievement.name : "Không đeo",
                        ],
                        ["Số danh hiệu có", player.achievements],
                        [
                          "Đang ở",
                          online && location ? (
                            <Tooltip key="loc" title={`Toạ độ x ${location.x}, y ${location.y}`}>
                              <span>{`${location.mapName} khu ${location.zoneId}`}</span>
                            </Tooltip>
                          ) : (
                            "Không trong game"
                          ),
                        ],
                        ["Đăng nhập gần nhất", formatDate(player.loginDate)],
                        [
                          "Online lần cuối",
                          online ? "Đang trong game" : formatDate(player.lastOnline),
                        ],
                        ["Quà gộp server", player.isMergeServer ? "Đã nhận" : "Chưa nhận"],
                      ]}
                    />
                  ) : (
                    <MDTypography variant="button" color="text">
                      Tài khoản này chưa tạo nhân vật.
                    </MDTypography>
                  )}
                </PageCard>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <PageCard title="Điểm" color="success">
                  {player ? (
                    <InfoList
                      rows={[
                        ["Điểm tích luỹ lôi đài", formatNumber(player.accumulatedPoint)],
                        [
                          `Mùa lôi đài hiện tại (mùa ${player.arenaSeasonId})`,
                          `${formatNumber(player.arenaSeasonPoints)} điểm · ${
                            player.arenaSeasonWins
                          } thắng, ${player.arenaSeasonLosses} thua`,
                        ],
                        ["Điểm sự kiện", formatNumber(player.eventPoint)],
                        ["Điểm PK (giết người)", player.pkPoint],
                      ]}
                    />
                  ) : (
                    <MDTypography variant="button" color="text">
                      Chưa có nhân vật nên chưa có điểm.
                    </MDTypography>
                  )}
                </PageCard>
              </Grid>
              <Grid item xs={12} lg={4}>
                <PageCard title="Tài khoản" color="dark">
                  <InfoList
                    rows={[
                      ["Tên đăng nhập", account.username],
                      ["Email", account.email || "Chưa có"],
                      ["Điện thoại", account.phone || "Chưa có"],
                      ["Ngày tạo", formatDate(account.createDate)],
                      ["Địa chỉ mạng lúc tạo (IP)", account.ipv4Create || "-"],
                      account.banned && ["Lý do bị khoá", account.banReason || "Không ghi"],
                      chatMute && ["Lý do khoá chat", chatMute.reason || "Không ghi"],
                    ]}
                  />
                </PageCard>
              </Grid>
              {online && buff && (
                <Grid item xs={12} md={6}>
                  <PageCard
                    title="Buff tạm"
                    subtitle="Tự mất khi người chơi thoát game"
                    color="warning"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={buff.enchant}
                          disabled={busy || !canGive}
                          onChange={(e) => toggleBuff("enchant", e.target.checked)}
                        />
                      }
                      label={`Đập đồ không bao giờ thất bại — ${
                        buff.enchant ? "đang bật" : "đang tắt"
                      }`}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={buff.tattoo}
                          disabled={busy || !canGive}
                          onChange={(e) => toggleBuff("tattoo", e.target.checked)}
                        />
                      }
                      label={`Cường hoá xăm không bao giờ thất bại — ${
                        buff.tattoo ? "đang bật" : "đang tắt"
                      }`}
                    />
                  </PageCard>
                </Grid>
              )}
              <Grid item xs={12} md={online && buff ? 6 : 12}>
                <PageCard
                  title="Đăng nhập gần đây"
                  color="secondary"
                  noPadding
                  actions={
                    logins.length > LOGIN_PREVIEW && (
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="white"
                        onClick={() => setShowAllLogins((v) => !v)}
                      >
                        {showAllLogins ? "Thu gọn" : `Xem cả ${logins.length} lần`}
                      </MDButton>
                    )
                  }
                >
                  <AdminTable
                    rows={showAllLogins ? logins : logins.slice(0, LOGIN_PREVIEW)}
                    rowKey={(l, i) => `${l.time}-${i}`}
                    emptyText="Chưa có lần đăng nhập nào"
                    columns={[
                      { key: "time", label: "Thời gian", render: (l) => formatDate(l.time) },
                      { key: "ip", label: "Địa chỉ mạng (IP)" },
                      {
                        key: "success",
                        label: "Kết quả",
                        render: (l) => (
                          <MDBox display="flex" gap={0.5}>
                            <Chip
                              size="small"
                              color={l.success ? "success" : "error"}
                              label={l.success ? "Vào được" : "Không vào được"}
                            />
                            {l.web && <Chip size="small" label="Qua web" />}
                          </MDBox>
                        ),
                      },
                    ]}
                  />
                </PageCard>
              </Grid>
            </Grid>

            {advancedRows.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <MDBox
                  component="button"
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  aria-expanded={advancedOpen}
                  px={2}
                  py={1.5}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  width="100%"
                  sx={{
                    cursor: "pointer",
                    border: 0,
                    background: "transparent",
                    textAlign: "left",
                    color: "inherit",
                  }}
                >
                  <Icon color="error">warning_amber</Icon>
                  <MDBox flex={1}>
                    <MDTypography variant="button" fontWeight="bold" display="block">
                      Nâng cao — chỉ dùng khi chắc chắn
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Đặt mật khẩu, quà gộp server, quyền admin, xoá nhân vật / tài khoản
                    </MDTypography>
                  </MDBox>
                  <Icon>{advancedOpen ? "expand_less" : "expand_more"}</Icon>
                </MDBox>
                <Collapse in={advancedOpen}>
                  <MDBox px={2} pb={1}>
                    {advancedRows.map((row, index) => (
                      <MDBox key={row.key}>
                        {index > 0 && <Divider sx={{ my: 0 }} />}
                        {row}
                      </MDBox>
                    ))}
                  </MDBox>
                </Collapse>
              </Card>
            )}
          </>
        )}

        {tab === "inventory" && player && (
          <PageCard title="Hành trang" noPadding>
            {online && canGive && <OnlineEditNote />}
            <InventoryTab
              player={player}
              onEdit={
                canGive && !online
                  ? (inventory, item) => setAssetDialog({ kind: "editItem", inventory, item })
                  : null
              }
              onRemove={
                canGive
                  ? (inventory, item) => setAssetDialog({ kind: "removeItem", inventory, item })
                  : null
              }
            />
          </PageCard>
        )}

        {tab === "achievements" && player && (
          <PageCard
            title="Danh hiệu"
            noPadding
            actions={
              canGive && (
                <Tooltip title={online ? "Chờ người chơi thoát game rồi mới cấp được" : ""}>
                  <span>
                    <MDButton
                      size="small"
                      variant="gradient"
                      color="info"
                      disabled={online}
                      onClick={() => setAssetDialog({ kind: "achievement" })}
                    >
                      <Icon>military_tech</Icon>&nbsp;Cấp danh hiệu
                    </MDButton>
                  </span>
                </Tooltip>
              )
            }
          >
            {online && canGive && <OnlineEditNote />}
            <AdminTable
              rows={player.achievementList || []}
              rowKey="id"
              emptyText="Chưa có danh hiệu nào"
              columns={[
                {
                  key: "name",
                  label: "Danh hiệu",
                  render: (a) => (
                    <MDBox display="flex" gap={1} alignItems="center">
                      <Tooltip title={`mã ${a.id} · mã mẫu ${a.templateId}`}>
                        <span>{a.name}</span>
                      </Tooltip>
                      {a.current && <Chip size="small" color="info" label="Đang đeo" />}
                    </MDBox>
                  ),
                },
                {
                  key: "expire",
                  label: "Hết hạn",
                  render: (a) => (a.expire ? formatDate(a.expire) : "Vĩnh viễn"),
                },
                canGive && {
                  key: "actions",
                  label: "",
                  align: "right",
                  render: (a) => (
                    <Tooltip title="Thu hồi danh hiệu này">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Thu hồi"
                        disabled={busy}
                        onClick={() => removeAchievement(a)}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  ),
                },
              ].filter(Boolean)}
            />
          </PageCard>
        )}

        {tab === "pets" && player && (
          <PageCard title="Pet" noPadding>
            {online && canGive && <OnlineEditNote />}
            <AdminTable
              rows={player.pets}
              rowKey="petId"
              emptyText="Chưa có pet nào"
              columns={[
                {
                  key: "name",
                  label: "Pet",
                  render: (p) => (
                    <MDBox display="flex" gap={1} alignItems="center">
                      <Tooltip title={`mã pet ${p.petId}`}>
                        <span>{p.name}</span>
                      </Tooltip>
                      {p.name !== p.templateName && p.templateName && (
                        <MDTypography variant="caption" color="text">
                          ({p.templateName})
                        </MDTypography>
                      )}
                      {p.isSelected && <Chip size="small" color="info" label="Đang dắt theo" />}
                    </MDBox>
                  ),
                },
                { key: "lvl", label: "Cấp", align: "right" },
                { key: "star", label: "Sao", align: "right" },
                {
                  key: "stats",
                  label: "Sức / Nhanh / Trí",
                  render: (p) => `${p.str} / ${p.agi} / ${p._int}`,
                },
                {
                  key: "hp",
                  label: "Máu (HP)",
                  align: "right",
                  render: (p) => `${formatNumber(p.hp)} / ${formatNumber(p.maxHp)}`,
                },
                {
                  key: "points",
                  label: "Điểm tiềm năng / kỹ năng",
                  align: "right",
                  render: (p) => `${p.tiemnangPoint ?? 0} / ${p.skillPoint ?? 0}`,
                },
                {
                  key: "hiddenStats",
                  label: "Chỉ số ẩn",
                  render: (p) =>
                    p.hiddenStats?.length ? (
                      <MDBox display="flex" gap={0.5} flexWrap="wrap">
                        {p.hiddenStats.map((h, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Chip key={i} size="small" label={h} />
                        ))}
                      </MDBox>
                    ) : (
                      "-"
                    ),
                },
                {
                  key: "isUpTier",
                  label: "Đã tiến hoá",
                  render: (p) => (p.isUpTier ? "Rồi" : "Chưa"),
                },
                {
                  key: "expire",
                  label: "Hết hạn",
                  render: (p) => (p.expire ? formatDate(p.expire) : "Vĩnh viễn"),
                },
                canGive && {
                  key: "actions",
                  label: "",
                  align: "right",
                  render: (p) => (
                    <Tooltip
                      title={
                        online
                          ? "Chờ người chơi thoát game rồi mới sửa được"
                          : "Sửa cấp, sao, điểm của pet"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          aria-label="Sửa pet"
                          disabled={online}
                          onClick={() => setAssetDialog({ kind: "pet", pet: p })}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </span>
                    </Tooltip>
                  ),
                },
              ].filter(Boolean)}
            />
          </PageCard>
        )}

        {tab === "vip" && canVip && (
          <PageCard title="VIP" color="warning" noPadding>
            <PlayerVipTab userId={userId} />
          </PageCard>
        )}
        {tab === "petcoin" && (
          <PageCard
            title="Sổ Pet Coin"
            subtitle="Mọi lần cộng / trừ Pet Coin của tài khoản"
            color="warning"
            noPadding
          >
            <PetCoinTab userId={userId} reloadKey={petCoinKey} />
          </PageCard>
        )}
        {tab === "mailbox" && player && (
          <PageCard title="Hòm thư" subtitle="Gồm cả thư toàn server người này nhìn thấy" noPadding>
            <MailboxTab userId={userId} />
          </PageCard>
        )}

        {tab === "boss" && (
          <PageCard title="Các trận boss đã đánh" color="error" noPadding>
            <BossTab userId={userId} />
          </PageCard>
        )}

        {tab === "history" && canHistory && (
          <PageCard
            title="Lịch sử"
            subtitle="Hoạt động của người chơi và mọi thao tác admin lên người này"
            color="secondary"
            noPadding
          >
            <HistoryTab userId={userId} />
          </PageCard>
        )}
      </MDBox>
      <Footer />

      {dialog === "ban" && <BanDialog userId={userId} onClose={closeDialog} onDone={refresh} />}
      {dialog === "mute" && <MuteDialog userId={userId} onClose={closeDialog} onDone={refresh} />}
      {dialog === "currency" && (
        <CurrencyDialog
          userId={userId}
          onClose={closeDialog}
          onDone={refresh}
          current={{
            gold: player?.gold,
            coin: player?.coin,
            lua: player?.lua,
            star: player?.star,
          }}
        />
      )}
      {dialog === "petcoin" && (
        <PetCoinDialog
          userId={userId}
          balance={account.vnd || 0}
          onClose={closeDialog}
          onDone={() => {
            refresh();
            setPetCoinKey((k) => k + 1);
          }}
        />
      )}
      {dialog === "item" && (
        <ItemDialog
          userId={userId}
          isAdmin={!!player?.isAdmin}
          onClose={closeDialog}
          onDone={refresh}
        />
      )}
      {dialog === "pet" && <PetDialog userId={userId} onClose={closeDialog} onDone={refresh} />}
      {dialog === "buffItem" && (
        <BuffItemDialog userId={userId} onClose={closeDialog} onDone={refresh} />
      )}
      {dialog === "message" && (
        <MessageDialog userId={userId} onClose={closeDialog} onDone={refresh} />
      )}
      {dialog === "teleport" && (
        <TeleportDialog userId={userId} onClose={closeDialog} onDone={refresh} />
      )}
      {assetDialog?.kind === "removeItem" && (
        <RemoveItemDialog
          userId={userId}
          inventory={assetDialog.inventory}
          item={assetDialog.item}
          onClose={closeAssetDialog}
          onDone={refresh}
        />
      )}
      {assetDialog?.kind === "editItem" && (
        <EditItemDialog
          userId={userId}
          inventory={assetDialog.inventory}
          item={assetDialog.item}
          onClose={closeAssetDialog}
          onDone={refresh}
        />
      )}
      {assetDialog?.kind === "pet" && (
        <EditPetDialog
          userId={userId}
          pet={assetDialog.pet}
          onClose={closeAssetDialog}
          onDone={refresh}
        />
      )}
      {assetDialog?.kind === "achievement" && (
        <AchievementDialog userId={userId} onClose={closeAssetDialog} onDone={refresh} />
      )}
      {dialog === "password" && (
        <PasswordDialog
          userId={userId}
          username={account.username}
          onClose={closeDialog}
          onDone={refresh}
        />
      )}
      {dialog === "delete" && (
        <DeletePlayerDialog
          userId={userId}
          name={player?.name || null}
          username={account.username}
          onClose={closeDialog}
          onDone={() => navigate("/players", { replace: true })}
        />
      )}
    </DashboardLayout>
  );
}

export default PlayerDetail;
